import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';
import { numberToWords } from '../utils/numberUtils';

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf"),
  fontStyle: 'normal',
  fontWeight: 'normal'
});

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf"),
  fontStyle: 'italic',
  fontWeight: 'normal'
});

const ContractPDFSimple = React.memo(({ contract }) => {
  // Obtenir le branding selon le site de l'employé
  const BRAND = getBrandingForEmployee(contract.employee);
  
  // Extraire les données de l'employé avec sécurité
  const employee = contract.employee || {};
  
  // Fonction de formatage de date sécurisée
  const safeFormatDate = (date) => {
    if (!date) {
      return '';
    }
    
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      const result = `${day}-${month}-${year}`;
      return result;
    } catch (error) {
      return '';
    }
  };
  
  // Fonction pour sécuriser les chaînes
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  // Fonction de validation pour les valeurs numériques
  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '' || value === false) {
      return defaultValue;
    }
    
    const num = Number(value);
    if (isNaN(num) || !isFinite(num) || num < 0) {
      return defaultValue;
    }
    
    // Limiter à une valeur raisonnable
    if (num > 1000000000) {
      return 1000000000;
    }
    
    return num;
  };
  
  // Récupération des informations de l'employé
  const nom = contract.last_name || contract.employee?.last_name || employee.last_name || '';
  const prenom = contract.first_name || contract.employee?.first_name || employee.first_name || '';
  const nomArabic = contract.nom_arabic || contract.employee?.nom_arabic || employee.nom_arabic || '';
  const prenomArabic = contract.prenom_arabic || contract.employee?.prenom_arabic || employee.prenom_arabic || '';
  const adresseArabic = contract.adresse_arabic || contract.employee?.adresse_arabic || employee.adresse_arabic || '';
  const dateNaissance = employee.date_naissance ? safeFormatDate(employee.date_naissance) : safeFormatDate(contract.date_naissance) || '';
  const lieuNaissance = contract.lieu_naissance || contract.employee?.lieu_naissance || employee.lieu_naissance || '';
  const adresse = contract.adresse || contract.employee?.adresse || employee.adresse || '';
  const situationFamiliale = contract.situation_familiale || contract.employee?.situation_familiale || employee.situation_familiale || '';
  const email = contract.email || contract.employee?.email || employee.email || '';
  const telephone = contract.phone || contract.employee?.phone || employee.phone || '';
  const poste = contract.poste || contract.employee?.poste || employee.poste || '';
  const salaire = safeNumber(contract.salaire || contract.employee?.salaire || employee.salaire, 0);
  const periodeEssaiMois = safeNumber(contract.periode_essai_mois || contract.employee?.periode_essai_mois || employee.periode_essai_mois, 0);
  const renouvelableFois = safeNumber(contract.renouvelable_fois || contract.employee?.renouvelable_fois || employee.renouvelable_fois, 0);
  const dureePreavisMois = safeNumber(contract.duree_preavis_mois || contract.employee?.duree_preavis_mois || employee.duree_preavis_mois, 0);
  
  // Fonction de traduction de la situation familiale en arabe
  const translateSituationFamilialeArabe = (situation) => {
    const translations = {
      'Célibataire': 'أعزب(ة)',
      'Marié(e)': 'متزوج(ة)',
      'Divorcé(e)': 'مطلق(ة)',
      'Veuf(ve)': 'أرمل(ة)'
    };
    return translations[situation] || situation;
  };
  
  // Fonction de formatage sécurisé pour l'affichage
  const safeDisplay = (value, suffix = '') => {
    const safeVal = safeNumber(value, 0);
    return `${safeVal}${suffix}`;
  };
  
  // Convertir le salaire en lettres
  let salaireLettres = 'zéro';
  try {
    if (salaire > 0 && salaire <= 999999999) {
      salaireLettres = numberToWords(salaire) || 'zéro';
    }
  } catch (error) {
    salaireLettres = 'zéro';
  }
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>

            <View style={styles.logo}>
              <Image src={BRAND.pdfLogo} style={styles.logoImage} />
            </View>

            <View style={styles.headerRow}>
              <Text style={styles.headerReference}>
                Réf : {safeString(contract.reference)}
              </Text>
              <Text style={styles.headerDate}>
                Boufarik, le : {new Date().toLocaleDateString('fr-FR')}
              </Text>
            </View>           

          {/* Titre principal en arabe */}
          <View style={styles.arabicTitleContainer}>
            <Text style={styles.mainTitleArabic}>
              عقد العمل
            </Text>
          </View>

          <Text style={styles.mainTitle}>
            CONTRAT DE TRAVAIL
          </Text>

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>ENTRE-LES SOUSSIGNÉS :</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>بين الممضيين أسفله</Text>
            </View>
          </View>

          {/* SECTION EMPLOYEUR */}
          <View style={styles.formGroup}>
            <Text style={styles.sectionTitle}>L'EMPLOYEUR</Text>
            <Text style={styles.field}>La société : <Text style={styles.fieldValue}>2MP INDUSTRY SPA</Text></Text>
            <Text style={styles.field}>Sise à : <Text style={styles.fieldValue}>Lot 39, Cité Aidja, étage 2, Zone Industrielle de Boufarik, 09400, Blida, Algérie</Text></Text>
            <Text style={styles.field}>Email : <Text style={styles.fieldValue}>direction@2mp-industry.com</Text></Text>
            <Text style={styles.field}>Téléphone : <Text style={styles.fieldValue}>+ 213 25 28 39 90 | + 213 25 28 39 46</Text></Text>
            <Text style={styles.field}>Représentée par : <Text style={styles.fieldValue}>MOUSSAOUI Bilel</Text></Text>
            <Text style={styles.field}>Fonction : <Text style={styles.fieldValue}>Président du Conseil d'Administration</Text></Text>
          </View>

          {/* Section Employeur en arabe */}
          <View style={styles.arabicSection}>
            <Text style={styles.arabicSectionTitle}>المستخدم</Text>
            <Text style={styles.arabicLabel}>شر كة: 2 ام بي أندوستري</Text>
            <Text style={styles.arabicLabel}>المقر: القطعة 39، حي عيجة، الطابق الثاني، المنطقة الصناعية بوفاريك، 09400، البليدة، الجزائر</Text>
            <Text style={styles.arabicLabel}>البريد الالكتروني: direction@2mp-industry.com</Text>
            <Text style={styles.arabicLabel}>رقم هاتف المؤسسة : + 213 25 28 39 90 | + 213 25 28 39 46  </Text>
            <Text style={styles.arabicLabel}>الممثلة من طرف: السيد موساوي بلال</Text>
            <Text style={styles.arabicLabel}>المنصب: رئيس مجلس إدارة</Text>
          </View>

          <View style={styles.spacer} />

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>Ci-après dénommée "l'Employeur"</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>المسمى أدناه "المستخدم"</Text>
            </View>
          </View>

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>D'une part</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>من جهة</Text>
            </View>
          </View>

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>ET :</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>و:</Text>
            </View>
          </View>

          {/* SECTION EMPLOYEE */}
          <View style={styles.formGroup}>
            <Text style={styles.field}>Madame/Monsieur : <Text style={styles.fieldValue}>{safeString(nom)} {safeString(prenom)}</Text></Text>
            <Text style={styles.field}>né(e) le : <Text style={styles.fieldValue}>{dateNaissance}</Text> à <Text style={styles.fieldValue}>{safeString(lieuNaissance)}</Text></Text>
            <Text style={styles.field}>Demeurent à : <Text style={styles.fieldValue}>{safeString(adresse)}</Text></Text>
            <Text style={styles.field}>Situation familiale : <Text style={styles.fieldValue}>{safeString(situationFamiliale)}</Text></Text>
            <Text style={styles.field}>Adresse é-mail : <Text style={styles.fieldValue}>{safeString(email)}</Text></Text>
            <Text style={styles.field}>Numéro de téléphone : <Text style={styles.fieldValue}>{safeString(telephone)}</Text></Text>
          </View>

          {/* Section Employee en arabe */}
          <View style={styles.arabicSection}>
            <Text style={styles.arabicLabel}>السيد (ة) : {safeString(nomArabic)} {safeString(prenomArabic)}</Text>
            <Text style={styles.arabicLabel}>المولود (ة) بتاريخ: {dateNaissance}</Text>
            <Text style={styles.arabicLabel}>الساكن (ة): {safeString(adresseArabic)}</Text>
            <Text style={styles.arabicLabel}>الحالة العائلية: {translateSituationFamilialeArabe(situationFamiliale)}</Text>
            <Text style={styles.arabicLabel}>البريد الالكتروني: {safeString(email)}</Text>
            <Text style={styles.arabicLabel}>رقم الهاتف: {safeString(telephone)}</Text>
          </View>

          <View style={styles.spacer} />

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>Ci-après dénommée "l'Employée"</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>المسمى أدناه "الموظف"</Text>
            </View>
          </View>

          <View style={styles.bilingualRow}>
            <View style={styles.leftColumn}>
              <Text style={styles.separatorLeft}>D'autre part</Text>
            </View>
            <View style={styles.rightColumn}>
              <Text style={styles.arabicLabelRight}>من جهة أخرى</Text>
            </View>
          </View>
          <Text style={styles.content}>
Ci-après dénommées ensemble les « Parties » et séparément la « Partie ».
          </Text>
          <Text style={styles.arabicContent}>
يشار إليهما فيما يلي معًا باسم "الأطراف" وبشكل منفصل باسم "الطرف".
          </Text>

          {/* ARTICLE 1 */}
          <Text style={styles.articleTitle}>ARTICLE 1 - CADRE LÉGISLATIF ET RÉGLEMENTAIRE</Text>
          <Text style={styles.content}>
            Le présent Contrat est conclu conformément aux lois en vigueur en Algérie notamment la Loi n°90-11 du 21 avril 1990 complétée et modifiée, relative aux relations de travail et la loi 18-07 relative à la protection des personnes physiques dans le traitement de leur donnée à caractère personnel et les textes subséquents.
          </Text>
          
          <View style={styles.arabicArticleContainer}>
            <Text style={styles.arabicArticle}>المادة 01- الإطار التشريعي والتنظيمي</Text>
            <Text style={styles.arabicContent}>
              تم إبرام هذا العقد طبقا للقوانين النافذة في الجزائر لاسيما القانون رقم 90-11 المؤرخ 21 أبريل 1990، المكمل والمعدل، المتعلق بعلاقات العمل والقانون 18-07 المتعلق بحماية الأشخاص الطبيعيين في معالجة بياناتهم الشخصية والنصوص اللاحقة بهما.
            </Text>
          </View>

                    <Text style={styles.articleTitle}>ARTICLE 2 - OBJET DU CONTRAT</Text>
          <Text style={styles.content}>
            Le présent Contrat a pour objet de définir les conditions de l'emploi et de travail de l'Employée dans le cadre d'une relation de travail à durée indéterminée.
          </Text>
          <Text style={styles.content}>
            Par le présent Contrat, l'Employée est recrutée en qualité : <Text style={styles.highlight}>{safeString(poste)}</Text>
          </Text>
          <Text style={styles.arabicArticle}>المادة 02 - موضوع العقد</Text>
          <Text style={styles.arabicContent}>
            يهدف هذا العقد إلى تحديد شروط توظيف وعمل الموظف في إطار علاقة عمل غير محددة المدة.
          </Text>
          <Text style={styles.arabicContent}>
            بموجب هذا العقد، يتم تعيين الموظف بصفة: {safeString(poste)}
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 3 - DURÉE DU CONTRAT</Text>
          <Text style={styles.content}>
            Le présent Contrat est conclu pour une durée indéterminée et à temps plein, sous réserve que l'Employé fournisse tous les documents et informations qui lui sont demandées et nécessaires à la constitution de son dossier administratif et d'un examen médical satisfaisant.
          </Text>
          <Text style={styles.content}>            Il prend effet à compter du : <Text style={styles.highlight}>{safeFormatDate(contract.hire_date) || ".............."}</Text>

          </Text>
          <Text style={styles.arabicArticle}>المادة 03- مدة العقد</Text>

          <Text style={styles.arabicContent}>
            تم إبرام هذا العقد لمدة غير محددة وعلى أساس دوام كامل، بشرط أن يقدم الموظف جميع الوثائق والمعلومات المطلوبة والضرورية لتشكيل ملفه الإداري والفحص الطبي المرضي.
          </Text>
          <Text style={styles.arabicContent}>
            يسري مفعوله بدءا من تاريخ: {safeFormatDate(contract.hire_date) || ".............."}
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 4 - MISSION ET RESPONSABILITÉS DU POSTE</Text>
          <Text style={styles.content}>
            L'Employée aura à exécuter les missions, tâches et responsabilités qui lui sont confiées, conformément à l'Annexe 1 et ce, conformément aux règles et normes professionnelles en vigueur au sein de l'organisme Employeur.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 04- مهمة ومسؤوليات المنصب</Text>
<Text style={styles.arabicContent}>
            يتوجب على الموظف تنفيذ المهام والواجبات والمسؤوليات الموكلة إليه وفقاً للملحق رقم 1 ووفقاً للقواعد والمعايير المهنية المعمول بها في الشركة.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 5 - LIEU DE TRAVAIL</Text>
          <Text style={styles.content}>
            L'Employée exercera ses fonctions au niveau du siège et à l'unité de production.
          </Text>
          <Text style={styles.warning}>
            Tout manquement aux obligations ci-dessus constituera un motif de résiliation de plein droit du présent Contrat.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 05 - مكان العمل</Text>

          <Text style={styles.arabicContent}>
            يمارس الموظف مهامه في مقر الشركة ووحدة الإنتاج.
          </Text>
          <Text style={styles.arabicWarning}>
            أي اخلال في الالتزامات المذكورة أعلاه يشكل سببا لإنهاء هذا العقد تلقائيا.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 6 - HORAIRES DE TRAVAIL</Text>
          <Text style={styles.content}>
            Les horaires de travail seront de 08 heures à 12 heures, et de 13 heures à 16 heures 30 mn.
          </Text>
          <Text style={styles.content}>
            Ces horaires correspondent à un volume horaire hebdomadaire dans les conditions normales de travail.
          </Text>
          <Text style={styles.content}>
            Toutefois, l'Employeur pourrait demander à l'Employée d'accomplir en dehors de ses heures de travail, en cas de nécessité de service et après l'obtention de l'accord du responsable hiérarchique de l'Employé, des heures supplémentaires qui ne pourraient excéder 20 % de ladite durée légale de travail.
          </Text>
          <Text style={styles.warning}>
            Tout manquement aux obligations ci-dessus constituera un motif de résiliation de plein droit du présent Contrat.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 06 - أوقات العمل</Text>

          <Text style={styles.arabicContent}>
            ستكون ساعات العمل من الساعة 8 صباحًا حتى الساعة 12، ومن الساعة 13 حتى الساعة 16:30.
          </Text>
          <Text style={styles.arabicContent}>
            تتوافق هذه الساعات مع حجم العمل بالساعة الأسبوعي في ظل ظروف العمل العادية.
          </Text>
          <Text style={styles.arabicContent}>
            ومع ذلك يجوز لصاحب العمل أن يطلب من العامل العمل ساعات إضافية خارج ساعات العمل إذا كانت هناك ضرورة لذلك للخدمة وذلك بعد الحصول على موافقة مسؤوله المباشر بما لا يتجاوز 20% من ساعات العمل القانونية.
          </Text>
          <Text style={styles.arabicWarning}>
            إن أي اخلال في الامتثال للالتزامات المذكورة أعلاه يشكل سبباً لإنهاء هذا العقد تلقائياً.
          </Text>
          

          <Text style={styles.articleTitle}>ARTICLE 7 - PÉRIODE D'ESSAI</Text>
          <Text style={styles.content}>
            Conformément aux dispositions légales en la matière, l'employé sera soumis à une période d'essai de ({safeDisplay(periodeEssaiMois)}) mois, de travail effectif, renouvelable ({safeDisplay(renouvelableFois)}) fois d'égale durée, durant laquelle les deux parties du présent contrat peuvent mettre fin à la relation de travail, sans préavis ni indemnité.
          </Text>
          <Text style={styles.content}>
            Passé ce délai et pour toute modification de la forme et/ou du fond du présent contrat, les deux parties devront s'astreindre aux obligations légales en la matière.
          </Text>

          </View>
      </Page>
      
      {/* Nouvelle page */}
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <Text style={styles.arabicArticle}>المادة 07: الفترة التجريبية</Text>

          <Text style={styles.arabicContent}>
            طبقاً للأحكام القانونية في هذا المجال يخضع الموظف لفترة تجريبية مدتها ({safeDisplay(periodeEssaiMois)}) شهراً من العمل الفعلي، قابلة للتجديد ({safeDisplay(renouvelableFois)}) مرة لمدة متساوية، وخلال هذه الفترة يجوز لكلا طرفي هذا العقد إنهاء علاقة العمل، دون إشعار أو تعويض. بعد هذه الفترة ولأي تعديل في شكل و/أو مضمون هذا العقد، يتعين على الطرفين الامتثال للالتزامات القانونية في هذا المجال.
          </Text>

          <View style={styles.bilingualRow}>
            <Text style={styles.articleTitle}> ARTICLE 8 - DROITS ET OBLIGATIONS DE L'EMPLOYÉ </Text>
            <Text style={styles.arabicArticle}> المادة 08: حقوق وواجبات الموظف </Text>
          </View>

          <Text style={styles.subArticle}>8.1. Droits de l'Employé :</Text>

          <Text style={styles.content}>
            Les droits de l'Employée se limitent strictement à ceux qui sont stipulés dans le présent Contrat, en conséquence, l'Employé ne saurait prétendre à aucun paiement, indemnité, compensation ou autre avantage qui ne sont pas expressément prévus dans le présent Contrat.
          </Text>
          <Text style={styles.content}>
            Néanmoins, durant sa relation de travail avec l'Employeur, l'Employée bénéficiera, conformément à la loi, d'un congé annuel dont l'époque sera déterminée par accord entre les Parties compte tenu des nécessités de service.
          </Text>
          
            <Text style={styles.arabicArticle}>8.1 حقوق الموظف</Text>

          <Text style={styles.arabicContent}>
            تقتصر حقوق الموظف بشكل صارم على تلك المنصوص عليها في هذا العقد، وبالتالي لا يجوز للموظف المطالبة بأي مدفوعات، أو تعويضات أو مكافآت أو أي منفعة أخرى غير منصوص عليها صراحة في هذا العقد.
          </Text>
          <Text style={styles.arabicContent}>
            ومع ذلك، فإن الموظف يستفيد خلال علاقته الوظيفية مع المستخدم، وفقاً للقانون، من إجازة سنوية، يتم تحديد مدتها بالاتفاق بين الطرفين مع مراعاة متطلبات الخدمة.
          </Text>
          
          <Text style={styles.subArticle}>8.2. Obligations de l'Employée :</Text>
          <Text style={styles.content}>
            Pendant la durée du présent Contrat, l'Employée est tenue au strict respect des dispositions de la législation du travail et du règlement intérieur de l'Employeur.
          </Text>
          <Text style={styles.content}>
            L'Employée s'engage à :
          </Text>
          <Text style={styles.listItem}>• S'astreindre à remplir et accomplir, au mieux de ses capacités l'ensemble des tâches et missions lui incombant dans le cadre de sa relation de travail avec l'Employeur, ainsi que toutes autres missions ou tâches qui lui sont confiées par sa hiérarchie ;</Text>
          <Text style={styles.listItem}>• Se conformer aux horaires de travail en vigueur, tels que fixés par l'Employeur ;</Text>
          <Text style={styles.listItem}>• Assumer toutes ses responsabilités quant à la parfaite utilisation et préservation des moyens et outils mis à sa disposition par l'Employeur et ce à quelque titre que ce soit ;</Text>
          <Text style={styles.listItem}>• Respecter la discipline et l'organisation de travail de l'Employeur telles que définies par les règlements, décisions et procédures internes de l'Employeur ;</Text>
          <Text style={styles.listItem}>• S'interdire de tout usage des biens et moyens de l'Employeur à des fins autres que celles relevant des intérêts de l'Employeur ;</Text>
          <Text style={styles.listItem}>• Respecter scrupuleusement les principes de la confidentialité et s'astreindre à garder le secret le plus absolu à l'égard de tous faits, informations ou documents relatifs à l'activité professionnelle de l'Employeur ;</Text>
          <Text style={styles.listItem}>• Respecter et appliquer les prescriptions des règlements et instructions, actuels ou futurs de l'Employeur ;</Text>
          <Text style={styles.listItem}>• Veiller en permanence et en toutes circonstances à promouvoir et à protéger l'image et les intérêts de l'Employeur dans le cadre de ses fonctions, à consacrer tout son temps et toute sa capacité de travail à l'Employeur et à ne pas cumuler avec une autre activité salariée ou autre ;</Text>
          <Text style={styles.listItem}>• S'abstenir d'avoir directement ou indirectement tout intérêt commercial ou financier dans toute entreprise concurrente avec l'Employeur ou qui fournit des biens ou services à l'Employeur ou achète ceux-ci auprès de lui ;</Text>
          <Text style={styles.listItem}>• Respecter toutes les règles édictées par l'Employeur en matière d'hygiène, de sécurité et d'environnement ;</Text>
          <Text style={styles.listItem}>• Respecter le règlement intérieur de l'Employeur ;</Text>
          <Text style={styles.listItem}>• Appliquer des dispositions de la loi n° 18-07 relative à la protection des personnes physiques dans le traitement de leurs données à caractère personnel, et à faire preuve de discrétion et à ne pas divulguer toutes les données personnelles traitées par lui (elle) pendant et après avoir exercé ses fonctions, à moins que cela ne soit requis par la loi ou à la demande de la hiérarchie.</Text>
          <Text style={styles.listItem}>• Respecter la confidentialité des informations sur les collègues auxquelles il a accès dans le cadre de son travail et s'abstenir de divulguer ou d'utiliser ces informations en dehors du contexte professionnel approprié.</Text>
          
          <Text style={styles.warning}>
            Tout manquement à cet engagement est considéré comme faute grave entraînant un licenciement immédiat sans préavis, ni indemnité.
          </Text>

          <Text style={styles.arabicArticle}>8.2 التزامات الموظف</Text>

          <Text style={styles.arabicContent}>
            خلال مدة هذا العقد، يتعين على الموظف الالتزام الصارم بأحكام قانون العمل واللوائح الداخلية للمستخدم.
          </Text>
          <Text style={styles.arabicContent}>
            يلتزم الموظف بما يلي:
          </Text>
          <Text style={styles.arabicListItem}>- الالتزام بتنفيذ وإنجاز كافة المهام والواجبات الملقاة على عاتقه في إطار علاقة العمل مع المستخدم، إلى أقصى حد من قدرته، بالإضافة إلى كافة المهام والواجبات الأخرى الموكلة إليه من قبل سلطته السلمية.</Text>
          <Text style={styles.arabicListItem}>- الالتزام بساعات العمل المعمول بها، والتي يحددها المستخدم؛</Text>
          <Text style={styles.arabicListItem}>- تحمل كافة المسؤوليات فيما يتعلق بالاستخدام الأمثل والمحافظة على الوسائل والأدوات التي يوفرها له المستخدم، لأي سبب كان؛</Text>
          <Text style={styles.arabicListItem}>- احترام نظام وتنظيم العمل لدى المستخدم كما هو محدد في اللوائح والقرارات والإجراءات الداخلية لدى المستخدم؛</Text>
          <Text style={styles.arabicListItem}>- الامتناع عن أي استخدام لممتلكات وموارد المستخدم لأغراض أخرى غير تلك المتعلقة بمصالح هذا الأخير؛</Text>
          <Text style={styles.arabicListItem}>- احترام مبادئ السرية بدقة والالتزام بالحفاظ على أقصى قدر من السرية فيما يتعلق بجميع الحقائق أو المعلومات أو الوثائق المتعلقة بالنشاط المهني للمستخدم؛</Text>
          <Text style={styles.arabicListItem}>- احترام وتطبيق متطلبات اللوائح والتعليمات الحالية أو المستقبلية للمستخدم؛</Text>
          <Text style={styles.arabicListItem}>- ضمان تعزيز وحماية صورة ومصالح المستخدم في إطار واجباته في جميع الأوقات وفي جميع الظروف، وتخصيص كل وقته وكل قدرته على العمل للمستخدم وعدم الجمع بين ذلك وبين أي نشاط آخر بأجر أو غيره؛</Text>
          <Text style={styles.arabicListItem}>- الامتناع عن وجود أي مصلحة تجارية أو مالية مباشرة أو غير مباشرة في أي شركة تنافس المستخدم أو التي تقوم بتوريد السلع أو الخدمات المستخدم أو تشتريها منه؛</Text>
          <Text style={styles.arabicListItem}>- الالتزام بجميع القواعد الصادرة عن المستخدم فيما يتعلق بالنظافة والسلامة والبيئة؛</Text>
          <Text style={styles.arabicListItem}>- الالتزام باللوائح الداخلية المستخدم؛</Text>
          <Text style={styles.arabicListItem}>- الالتزام تطبيق أحكام القانون رقم 18-07 المتعلق بحماية الأشخاص الطبيعيين في معالجة بياناتهم الشخصية، وممارسة السرية التامة وعدم الكشف عن جميع البيانات الشخصية التي يعالجها أثناء وبعد ممارسة وظائفه، ما لم يكن ذلك مطلوبا بموجب القانون أو بناء على طلب من السلطة السلمية،</Text>
          <Text style={styles.arabicListItem}>- احترام سرية المعلومات الخاصة بالزملاء والتي يطلع عليها في إطار عمله، والامتناع عن الكشف عنها أو استخدامها خارج السياق المهني المناسب.</Text>
          
          <Text style={styles.arabicWarning}>
            ويعتبر أي إخفاق في الامتثال لهذا الالتزام مخافة جسيمة تؤدي إلى الفصل الفوري دون إشعار أو تعويض.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 9 - ABSENCES</Text>
          <Text style={styles.content}>
            Sauf les cas expressément prévus par la loi et règlements en vigueur ainsi que les notes et le règlement intérieur de l'Employeur, l'Employée ne peut être rémunérée pour une période non travaillée.
          </Text>
          <Text style={styles.content}>
            Toutefois, l'Employée peut bénéficier, sur la base de justificatifs et sous réserve d'accord préalable de l'Employeur, d'absences spéciales payées dans les conditions fixées par la loi et/ou la réglementation interne de l'Employeur.
          </Text>
          <Text style={styles.content}>
            En cas d'absence justifiée par une incapacité temporaire de travail résultant d'une maladie, l'employée doit aviser l'Employeur et transmettre dans un délai de 24 heures à compter du 1er jour de son indisponibilité un certificat médical prescrivant un arrêt de travail, ou la prolongation de celui-ci.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 9 - الغيابات</Text>

          <Text style={styles.arabicContent}>
            لا يجوز، باستثناء الحالات المنصوص عليها صراحة في القانون والأنظمة النافذة والمذكرات واللوائح الداخلية للمستخدم، دفع أجر العامل عن الفترة التي لم يعمل بها.
          </Text>
          <Text style={styles.arabicContent}>
            ومع ذلك، يجوز للموظف الاستفادة، على أساس المستندات الداعمة وبموافقة مسبقة من المستخدم، من غيابات خاصة مدفوعة الأجر بموجب الشروط المنصوص عليها في القانون و/أو اللوائح الداخلية للمستخدم.
          </Text>
          <Text style={styles.arabicContent}>
            في حالة الغياب المبرر بالعجز المؤقت عن العمل نتيجة المرض، يجب على العامل إخطار المستخدم وإرسال شهادة طبية خلال 24 ساعة من اليوم الأول لعدم تواجده تنص على التوقف عن العمل أو تمديده.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 10 - ABANDON DE POSTE</Text>
          <Text style={styles.content}>
            L'Employée, en vertu du présent Contrat, est tenue d'assurer ses fonctions sans discontinuité aucune.
          </Text>
          <Text style={styles.content}>
            En conséquence, en cas d'absence irrégulière prolongée de 15 jours ouvrables sans motif dûment reconnu, le présent Contrat est résilié aux torts exclusifs de l'Employé, nonobstant les poursuites judiciaires qui peuvent être engagées par l'organisme Employeur pour réparation des préjudices causés.
          </Text>
          <Text style={styles.content}>
            L'abandon de poste est établi au terme du présent Contrat, dès envoi de deux mises en demeures sans pour autant recevoir de nouvelles de l'intéressé.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 10- التخلي عن المنصب</Text>

          <Text style={styles.arabicContent}>
            يتوجب على الموظف بموجب هذا العقد أن يؤدي واجباته دون أي انقطاع.
          </Text>
          <Text style={styles.arabicContent}>
            وعليه، في حالة الغياب غير المنتظم لمدة 15 يوم عمل دون سبب واضح، يتم إنهاء هذا العقد بسبب خطأ الموظف الحصري، زيادة عن أي إجراءات قانونية قد ترفعها الشركة للمطالبة بالتعويض عن الأضرار الناجمة عن ذلك.
          </Text>
          <Text style={styles.arabicContent}>
            يتم تأسيس التخلي عن المنصب في حدود هذا العقد، بعد إرسال إشعارين رسميين دون تلقي أي أخبار من الشخص المعني.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 11 - RESTITUTION DES DOCUMENTS ET BIENS CONFIES ET PASSATION DE CONSIGNES</Text>
          <Text style={styles.arabicArticle}>المادة 11 - إرجاع الوثائق والبضائع الموكلة وتسليم المهام</Text>
          <Text style={styles.content}>
            En cas de résiliation du présent Contrat, pour quelle que cause que ce soit, l'Employée doit :
          </Text>
          
          <Text style={styles.subArticle}>11.1. Restitution des documents :</Text>
          <Text style={styles.content}>
            Restituer sans délai ni préavis à l'employeur, tout objet, documents et matériels qui auraient pu lui être remis pour l'accomplissement de sa mission.
          </Text>
          
          <Text style={styles.arabicArticle}>11.1 إرجاع المستندات</Text>

          <Text style={styles.arabicContent}>
            إعادة الى المستخدم أي أشياء أو مستندات أو مواد قد تكون أعطيت له لإنجاز مهمته، دون تأخير أو إخطار.
          </Text>
          
          <Text style={styles.subArticle}>11.2. Passation de consignes :</Text>
          <Text style={styles.content}>
            Procéder à la passation de consignes avec la personne désignée par l'organisme Employeur à cet effet, pour tous les dossiers dont il a la charge, les projets en cours ainsi que les démarches entreprises et les contacts en cours dans la prise en charge de ses missions et de remettre toutes les consignes et instructions qui lui ont été confiées de manière détaillée et par écrit, afin de garantir la continuité des activités et éviter toute perte d'information.
          </Text>
          <Text style={styles.content}>
            Le destinataire des consignes doit signer pour accuse des réceptions attestant de la bonne remise des informations.
          </Text>
          
          <Text style={styles.arabicArticle}>11.2 تسليم المهام</Text>
          <Text style={styles.arabicContent}>
            القيام بنقل التعليمات مع الشخص المعين من قبل الشركة لهذا الغرض، وذلك بالنسبة لجميع الملفات التي يكون مسؤولا عنها، والمشاريع الحالية وكذلك الخطوات المتخذة والاتصالات الحالية في إدارة مهامه و يتم تسليم جميع التعليمات و المهام الموكلة اليه بشكل مفصل و مكتوب من اجل ضمان استمرارية العمل و عدم حدوث أي خلل او فقدان للمعلومات.
          </Text>
          <Text style={styles.arabicContent}>
            يجب توقيع الطرف المستقبل على استلام هذه التعليمات كدليل على تسليمها
          </Text>
          
          <Text style={styles.subArticle}>11.3. Remboursement de prêt :</Text>
          <Text style={styles.content}>
            En cas de bénéfice de prêt, l'Employée s'engage à rembourser la totalité du montant restant du prêt sans délai et ce à la rupture de la relation de travail.
          </Text>
          
          <Text style={styles.arabicArticle}>11.3 سداد القرض</Text>
          
          <Text style={styles.arabicContent}>
            في حالة الاستفادة من القرض، يلتزم الموظف بسداد كامل المبلغ المتبقي من القرض دون تأخير وعند انتهاء علاقة العمل.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 12 - INDEPENDANCE ET CONFLITS D'INTERETS – NON-CONCURRENCE</Text>
          <Text style={styles.content}>
            L'Employée s'engage à se consacrer entièrement et exclusivement à l'exercice de ses fonctions et à la réalisation de ses tâches et activités en s'interdisant l'exercice d'activités rémunérées en dehors des tâches d'enseignement, de la participation à des organes délibérants d'entreprises publiques ou privés non concurrentes et la production d'œuvres artistiques, littéraires ou scientifiques sous réserve que ces activités n'entravent pas le bon fonctionnement et la réalisation des objectifs de l'entreprise.
          </Text>
          <Text style={styles.content}>
            L'Employée s'engage à ne pas avoir d'intérêts directs dans toute entreprise concurrente, cliente, sous-traitante ou fournisseur, et ne pas recevoir ou accepter pour quelque cause que ce soit des cadeaux, gratifications et/ou avantages de nature à compromettre son indépendance et entraver sa liberté de décision.
          </Text>
          <Text style={styles.content}>
            L'Employée s'engage à ne pas entreprendre des actions pouvant concurrencer l'Employeur dans son champ d'activités y compris dans les douze (12) mois qui suivent la date de cessation de la relation de travail.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 12 - الاستقلال وتضارب المصالح - عدم المنافسة</Text>

          <Text style={styles.arabicContent}>
            يلتزم الموظف بالتفرغ كلياً وحصرياً لممارسة وظائفه وإنجاز مهامه وأنشطته وذلك بمنعه من ممارسة أي نشاط مدفوع الأجر خارج مهام التدريس، أو المشاركة في الهيئات التداولية للشركات العامة أو الخاصة غير المتنافسة، أو إنتاج أعمال فنية أو أدبية أو علمية، بشرط ألا تعيق هذه الأنشطة حسن سير العمل وتحقيق أهداف الشركة.
          </Text>
          <Text style={styles.arabicContent}>
            يلتزم الموظف بعدم وجود له أي مصالح مباشرة مع أي شركة منافسة أو عميل أو مقاول من الباطن أو مورد، وعدم تلقي أو قبول لأي سبب من الأسباب هدايا أو إكراميات و/أو مزايا من شأنها المساس باستقلاليته وإعاقة حريته في اتخاذ القرار.
          </Text>
          <Text style={styles.arabicContent}>
            يلتزم الموظف بعدم القيام بأي أعمال من شأنها منافسة صاحب العمل في مجال نشاطه، بما في ذلك خلال الاثني عشر (12) شهرًا التالية لتاريخ انتهاء علاقة العمل.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 13 - CONFIDENTIALITE</Text>
          <Text style={styles.content}>
            L'Employée s'engage à ne pas divulguer d'informations d'ordre professionnel, relatives aux techniques, mode d'organisation et, de manière générale, tout document interne de l'Employeur.
          </Text>
          <Text style={styles.content}>
            L'Employée s'engage à ne pas divulguer directement ou indirectement à toutes personnes externes ou internes non habilitées, toutes données à caractère personnel et informations d'ordre professionnel considérées confidentielles ou à diffusion restreinte y compris dans les douze (12) mois qui suivent la date de la cessation de la relation de travail.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 13 - السرية</Text>

          <Text style={styles.arabicContent}>
            يلتزم الموظف بعدم الكشف عن أي معلومات مهنية تتعلق بالتقنيات، والأساليب التنظيمية، وبشكل عام، أي وثيقة داخلية للمستخدم.
          </Text>
          <Text style={styles.arabicContent}>
            يلتزم الموظف بعدم الكشف، بشكل مباشر أو غير مباشر، لأي أشخاص خارجيين أو داخليين غير مصرح لهم، عن أي بيانات شخصية ومعلومات مهنية تعتبر سرية أو مقيدة، بما في ذلك خلال الاثني عشر (12) شهرًا التالية لتاريخ انتهاء علاقة العمل.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 14 - RÉMUNÉRATION</Text>
          <Text style={styles.content}>
            En contrepartie de l'accomplissement de ses fonctions, l'Employée percevra un salaire mensuel d'un montant de :{safeDisplay(salaire)} DINAR ALGERIEN net
          </Text>
          
          <Text style={styles.content}>
            Le versement de la rémunération mensuelle à l'Employée sera effectué à terme échu à la fin de chaque mois après application des retenues au titre des cotisations de sécurité sociale et d'IRG prévues par la loi et ce sur une durée de 12 mois par année.
          </Text>
          <Text style={styles.content}>
            Le salaire mentionné ci-dessus ne couvre pas les heures supplémentaires de travail qui peuvent être demandées par l'Employeur et exécutées par l'Employée, si nécessaire pour le service. Ces heures supplémentaires ne pourraient excéder 20 % de ladite durée légale de travail en cas de nécessité de service. De ce fait, l'Employée peut percevoir une indemnité compensatoire.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 14: الأجر</Text>

          <Text style={styles.arabicContent}>
            في مقابل أداء واجباته، سيحصل الموظف على راتب شهري {safeDisplay(salaire)} دينار جزائري
          </Text>
          <Text style={styles.arabicContent}>
            يتم دفع الأجر الشهري للموظف على دفعات في نهاية كل شهر بعد خصم اشتراكات التأمينات الاجتماعية والضريبة على الدخل المنصوص عليها في القانون وذلك على مدى فترة 12 شهرا في السنة.
          </Text>
          <Text style={styles.arabicContent}>
            الراتب المذكور أعلاه لا يشمل ساعات العمل الإضافية التي قد يطلبها صاحب العمل ويقوم بها الموظف إذا لزم الأمر للخدمة. ولا يجوز أن تتجاوز هذه الساعات الإضافية 20% من ساعات العمل القانونية المذكورة في حالة وجود متطلبات خدمية. ونتيجة لذلك، قد يحصل الموظف على استحقاق تعويضي.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 15 - SUSPENSION ET CESSATION DE LA RELATION DE TRAVAIL</Text>
          <Text style={styles.content}>
            La cessation de la relation de travail, faisant l'objet du présent Contrat de travail, peut intervenir par l'effet de :
          </Text>
          <Text style={styles.listItem}>• L'Employée peut mettre fin au présent Contrat à tout moment en présentant sa démission sous réserve d'un préavis de ({safeDisplay(dureePreavisMois)}) mois, ou d'une durée moindre après consentement mutuel. L'Employeur peut le dispenser à sa seule discrétion.</Text>
          <Text style={styles.listItem}>• Le licenciement pour compression d'effectifs. Si l'Employeur est amené pour quelque raison que ce soit à réduire ses activités, ou suspendre une Partie de ses travaux, et par ce fait, à réduire ses effectifs, et au cas où l'Employée est concernée par cette procédure, il sera fait application des dispositions légales existant en la matière ;</Text>
          <Text style={styles.listItem}>• L'incapacité totale de travail, telle que définie par la législation ;</Text>
          <Text style={styles.listItem}>• La cessation de l'activité de l'Employeur ;</Text>
          <Text style={styles.listItem}>• La retraite de l'Employée ;</Text>
          <Text style={styles.listItem}>• Le décès de l'Employée ;</Text>
          <Text style={styles.listItem}>• La nullité ou l'abrogation légale du Contrat de travail.</Text>
          <Text style={styles.content}>
            L'Employeur se réserve le droit de mettre fin à tout moment au présent Contrat sans préavis ni indemnité en cas de faute grave ou de manquement caractérisé de l'Employée aux dispositions du présent Contrat, des lois et règlements en vigueur, et/ou à celles applicables au sein de l'organisme Employeur, et ce, dans le respect de la procédure disciplinaire en vigueur.
          </Text>
          <Text style={styles.content}>
            En cas de rupture du Contrat de travail pour tout autre motif que pour faute, le délai légal du préavis sera observé par l'Employeur.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 15 - تعليق وإنهاء علاقة العمل</Text>

          <Text style={styles.arabicContent}>
            قد يتم إنهاء علاقة العمل التي هي موضوع عقد العمل هذا نتيجة لما يلي:
          </Text>
          <Text style={styles.arabicListItem}>- يجوز للموظف إنهاء هذا العقد في أي وقت عن طريق تقديم استقالته مع إخطار مدته ({safeDisplay(dureePreavisMois)}) أشهر، أو فترة أقصر بعد الموافقة المتبادلة. يجوز لصاحب العمل الاستغناء عنها وفقًا لتقديره الخاص.</Text>
          <Text style={styles.arabicListItem}>- الفصل بسبب تقليص عدد الموظفين. إذا طُلب من المستخدم لأي سبب من الأسباب تقليص أنشطته أو تعليق جزء من عمله وبالتالي تقليص قوته العاملة، وفي حال تأثر الموظف بهذا الإجراء، فستطبق الأحكام القانونية الموجودة في هذا المجال؛</Text>
          <Text style={styles.arabicListItem}>- العجز الكلي عن العمل، كما هو محدد بالتشريعات؛</Text>
          <Text style={styles.arabicListItem}>- توقف نشاط صاحب العمل؛</Text>
          <Text style={styles.arabicListItem}>- تقاعد الموظف؛</Text>
          <Text style={styles.arabicListItem}>- وفاة الموظف؛</Text>
          <Text style={styles.arabicListItem}>- بطلان عقد العمل أو فسخه قانونيا.</Text>
          <Text style={styles.arabicContent}>
            يحتفظ المستخدم بالحق في إنهاء هذا العقد في أي وقت دون إشعار أو تعويض في حالة سوء السلوك الجسيم أو الإخلال الواضح من قبل الموظف بأحكام هذا العقد والقوانين واللوائح المعمول بها و/أو تلك المعمول بها داخل الشركة، مع الامتثال للإجراءات التأديبية المعمول بها.
          </Text>
          <Text style={styles.arabicContent}>
            في حالة إنهاء عقد العمل لأي سبب آخر غير سوء السلوك، يجب على صاحب العمل مراعاة فترة الإشعار القانونية.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 16 : TRAITEMENT DES DONNEES A CARACTERE PERSONNEL</Text>
          <Text style={styles.content}>
            En application de la loi n° 18-07 susvisée, l'employeur est tenu de protéger l'employé dans le traitement de toutes les données à caractère personnel le lui ont été confiés ou potentiellement consultées et de les protéger pendant et après la fin de sa relation de travail, ainsi que de lui garantir son droit d'information, d'accès, de rectification et d'opposition conformément aux articles 32 à 36 de la même loi.
          </Text>
          <Text style={styles.arabicArticle}>  المادة 16: معالجة المعلومات ذات الطابع الشخصي    </Text>

          <Text style={styles.arabicContent}>
            بموجب القانون رقم 18-07 المشار إليه أعلاه يلتزم المستخدم بحماية الموظف في معالجة كل المعطيات الشخصية المودعة لديه أو المحتمل الاطلاع عليها وحماية كل بياناته أثناء وبعد نهاية علاقة عمله في الشركة وكذا ضمان له الحق في الإعلام، الولوج، التصحيح والاعتراض طبقا للمواد من 32 إلى 36 من نفس القانون.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 17 : CONSENTEMENT POUR TRAITEMENT DES DONNEES A CARACTERE PERSONNEL</Text>
          <Text style={styles.content}>
            Après avoir été informé orienté et sensibilisé de tous ses droits quant à l'application de la loi 18-07 relative à la protection des personnes physiques dans le traitement de leurs données à caractère personnel, l'employé exprime son consentement explicite au traitement de ses données personnelles, dans les limites exigées par la relation de travail tout en conservant tous ses droits d'information, d'accès, de rectification et d'opposition.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 17: الموافقة على معالجة المعلومات ذات الطابع الشخصي</Text>

          <Text style={styles.arabicContent}>
            بعد إعلامه توجيهه وارشاده لجميع حقوقه فيما يخص تطبيق القانون 18-07 المتعلق بحماية الأشخاص الطبيعيين في مجال معالجة المعطيات ذات الطابع الشخصي يبدي العامل موافقته الصريحة لمعالجة معطياته الخاصة في حدود ما تقتضيه علاقة العمل مع الاحتفاظ بجميع حقوقه في الإعلام الولوج التصحيح والاعتراض.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 18 - MODIFICATION DU CONTRAT</Text>
          <Text style={styles.content}>
            Le présent Contrat ne peut être amendé, ni modifié que dans les formes qui ont présidé à son élaboration.
          </Text>
          <Text style={styles.content}>
            Toute modification du présent Contrat fera l'objet d'un avenant au présent Contrat.
          </Text>
          <Text style={styles.arabicArticle}>المادة 18: تعديل العقد</Text>

          <Text style={styles.arabicContent}>
            لا يجوز تعديل هذا العقد أو تغييره إلا بالصيغة التي تم صياغته بها.
          </Text>
          <Text style={styles.arabicContent}>
            سيكون أي تعديل لهذا العقد موضوع ملحق له.
          </Text>

        </View>
      </Page>
      
      {/* Troisième page */}
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <Text style={styles.articleTitle}>ARTICLE 19- DROIT APPLICABLE - REGLEMENT DES LITIGES</Text>
          <Text style={styles.content}>
            Le présent Contrat est régi par la législation et la réglementation en vigueur en Algérie.
          </Text>
          <Text style={styles.content}>
            Tout litige né de l'interprétation ou de l'exécution des dispositions du présent Contrat de travail, les Parties s'efforceront de le régler dans un cadre amiable, y compris toute procédure de conciliation prévue par la législation en vigueur.
          </Text>
          <Text style={styles.content}>
            A défaut d'un règlement à l'amiable, le litige sera soumis au tribunal territorialement compétent près du siège social de l'Employeur.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 19: القانون الواجب التطبيق - حل النزاعات</Text>

          <Text style={styles.arabicContent}>
            يخضع هذا العقد للقوانين والأنظمة المعمول بها في الجزائر.
          </Text>
          <Text style={styles.arabicContent}>
            في حالة حدوث أي نزاع ناتج عن تفسير أو تنفيذ أحكام عقد العمل هذا، فإن الطرفين سيعملان على حله وديًا، بما في ذلك أي إجراء توفيقي ينص عليه التشريع الساري.
          </Text>
          <Text style={styles.arabicContent}>
            في حالة عدم التوصل إلى تسوية ودية، يتم إحالة النزاع إلى المحكمة المختصة إقليمياً والتي يقع في نطاقها المقر الرئيسي للمستخدم.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 20- FIDELITE DE FORMATION</Text>
          <Text style={styles.content}>
            Dans le cadre de son développement professionnel et de la promotion des compétences, 2MP INDUSTRY SPA, finance les formations de ses employés.
          </Text>
          <Text style={styles.content}>
            La société couvrira les frais de formation liés aux programmes approuvés, y compris les frais d'inscription, les frais de matériel pédagogique et les éventuels frais de déplacement.
          </Text>
          <Text style={styles.content}>
            En contrepartie du financement de la formation, l'employé doit rester à l'entreprise pendant une durée de deux ans, sur accord de l'employeur.
          </Text>
          <Text style={styles.content}>
            En cas de départ avant la fin de cette période, l'employé sera tenu de rembourser à la société la totalité de la formation.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 20 – الالتزام بالوفاء بعد التكوين</Text>

          <Text style={styles.arabicContent}>
            في إطار تطويرها المهني وتعزيز الكفاءات، تتكفل شركة 2MP INDUSTRY SPA بتمويل الدورات التكوينية لفائدة موظفيها.
          </Text>
          <Text style={styles.arabicContent}>
            تشمل تغطية الشركة جميع التكاليف المتعلقة بالبرامج التكوينية المعتمدة، بما في ذلك رسوم التسجيل، وتكاليف المواد التعليمية، وكذا مصاريف التنقل إن وجدت.
          </Text>
          <Text style={styles.arabicContent}>
            مقابل هذا التمويل، يلتزم الموظف بالبقاء في المؤسسة لمدة سنتين، وذلك باتفاق مسبق مع صاحب العمل.
          </Text>
          <Text style={styles.arabicContent}>
            وفي حال مغادرة الموظف للشركة قبل انقضاء هذه المدة، فانه يكون ملزماً بردّ كامل التكاليف التي تكفلتها المؤسسة مقابل التكوين.
          </Text>

          <Text style={styles.articleTitle}>ARTICLE 21 - ENTREE EN VIGUEUR</Text>
          <Text style={styles.content}>
            Le présent Contrat entrera en vigueur dès sa signature entre les Parties et prendra effet à compter du : {safeFormatDate(employee.hire_date)}
          </Text>
          <Text style={styles.content}>
            Le présent Contrat est établi et signé en deux originaux, l'un conservé et l'autre remis à l'Employé.
          </Text>
                    <Text style={styles.arabicArticle}>المادة 21: بدايــــــة الســـــريان</Text>

          <Text style={styles.arabicContent}>
            يدخل هذا العقد حيز التنفيذ عند التوقيع عليه بين الطرفين ويصبح ساري المفعول اعتبارًا من: {safeFormatDate(employee.hire_date)}
          </Text>
          <Text style={styles.arabicContent}>
            تم تحرير هذا العقد وتوقيعه في نسختين أصليتين، يتم الاحتفاظ بإحداهما وتسليم الأخرى للموظف.
          </Text>

        </View>
      </Page>
      
      {/* Quatrième page - Signatures */}
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Signatures */}
          <View style={styles.signatureSection}>
            <Text style={styles.signatureTitle}>Fait à Boufarik, Blida, le :  .....................</Text>
            <Text style={styles.arabicSignature}>في بوفاريك البليدة يوم:  .....................</Text>
            
            <View style={styles.signatureContainer}>
              <View style={styles.signatureBox}>
                <View style={styles.bilingualRow}>
                  <View style={styles.leftColumn}>
                    <Text style={styles.separatorLeft}>L'Employé(e) :</Text>
                  </View>
                  <View style={styles.rightColumn}>
                    <Text style={styles.arabicLabelRight}>الموظف</Text>
                  </View>
                </View>
                <View style={styles.signatureLine}></View>
                <Text style={styles.signatureNote}>(Précédé de la mention manuscrite "Lu et approuvé")</Text>
                <Text style={styles.arabicSignatureNote}>(مسبوقة بعبارة: "قرأت ووافقت")</Text>
              </View>
              
              <View style={styles.signatureBox}>
                <View style={styles.bilingualRow}>
                  <View style={styles.leftColumn}>
                    <Text style={styles.separatorLeft}>L'Employeur :</Text>
                  </View>
                  <View style={styles.rightColumn}>
                    <Text style={styles.arabicLabelRight}>المستخدم</Text>
                  </View>
                </View>
                <Text style={styles.signatureName}>Monsieur Bilel MOUSSAOUI</Text>
                <Text style={styles.signaturePosition}>Président du Conseil d'Administration</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
});

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.5,
  },
  container: {
    flexGrow: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  headerReference: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  headerDate: {
    fontSize: 11,
    textAlign: 'right',
  },
  logo: {
    width: '100%',
    marginBottom: 20,
  },
  logoImage: {
    width: '100%',
    height: 60,
    objectFit: 'contain',
  },
  arabicTitleContainer: {
    marginBottom: 15,
    direction: 'rtl',
  },
  mainTitleArabic: {
    fontFamily: 'Amiri',
    fontSize: 22,
    textAlign: 'center',
    textDecoration: 'underline',
    fontWeight: 'bold',
    direction: 'rtl',
  },
  mainTitle: {
    fontSize: 16,
    textAlign: 'center',
    textDecoration: 'underline',
    fontWeight: 'bold',
    marginBottom: 20,
  },
  bilingualRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 5,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
    direction: 'rtl',
  },
  separatorLeft: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'left',
  },
  arabicLabelRight: {
    fontFamily: 'Amiri',
    fontSize: 13,
    textAlign: 'right',
    fontWeight: 'bold',
    direction: 'rtl',
  },
  formGroup: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 13,
    marginBottom: 10,
    textDecoration: 'underline',
  },
  field: {
    fontSize: 11,
    marginBottom: 3,
  },
  fieldValue: {
    fontWeight: 'normal',
  },
  arabicSection: {
    marginBottom: 15,
    direction: 'rtl',
  },
  arabicSectionTitle: {
    fontFamily: 'Amiri',
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'right',
    textDecoration: 'underline',
    direction: 'rtl',
  },
  arabicLabel: {
    fontFamily: 'Amiri',
    fontSize: 11,
    textAlign: 'right',
    marginBottom: 3,
    direction: 'rtl',
  },
  spacer: {
    height: 15,
  },
  articleTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 5,
    textDecoration: 'underline',
  },
  arabicArticleContainer: {
    marginTop: 10,
    direction: 'rtl',
  },
  arabicArticle: {
    fontFamily: 'Amiri',
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'right',
    direction: 'rtl',
  },
  content: {
    fontSize: 10,
    marginBottom: 6,
    textAlign: 'justify',
    lineHeight: 1.4,
  },
  arabicContent: {
    fontFamily: 'Amiri',
    fontSize: 11,
    marginBottom: 6,
    textAlign: 'right',
    lineHeight: 1.4,
    direction: 'rtl',
  },
  warning: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000',
    marginTop: 5,
    marginBottom: 8,
    textAlign: 'justify',
  },
  arabicWarning: {
    fontFamily: 'Amiri',
    fontSize: 11,
    fontWeight: 'bold',
    color: '#000',
    textAlign: 'right',
    marginBottom: 8,
    direction: 'rtl',
  },
  subArticle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
  },
  listItem: {
    fontSize: 10,
    marginLeft: 15,
    marginBottom: 4,
    textAlign: 'justify',
  },
  arabicListItem: {
    fontFamily: 'Amiri',
    fontSize: 10,
    marginRight: 15,
    marginBottom: 4,
    textAlign: 'right',
    direction: 'rtl',
  },
  signatureSection: {
    marginTop: 30,
    paddingTop: 20,
  },
  signatureTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'center',
  },
  arabicSignature: {
    fontFamily: 'Amiri',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 20,
    direction: 'rtl',
  },
  signatureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  signatureBox: {
    width: '45%',
  },
  signatureLine: {
    height: 1,
    backgroundColor: '#000',
    marginVertical: 40,
  },
  signatureNote: {
    fontSize: 9,
    fontStyle: 'italic',
    marginTop: 5,
  },
  arabicSignatureNote: {
    fontFamily: 'Amiri',
    fontSize: 10,
    fontStyle: 'italic',
    textAlign: 'right',
    marginTop: 5,
    direction: 'rtl',
  },
  signatureName: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 40,
  },
  signaturePosition: {
    fontSize: 10,
    marginTop: 5,
  },
});

export default ContractPDFSimple;